import { useEffect, useState } from "react";
import { api } from "../api";
import type { PageLink, PageSocialLink } from "../api/pages";
import { PageView, type PageViewData } from "../components/PageView";
import { recordPageAccess, getPageSubscriptionStatus } from "../api/pages";
import { SEO } from "../components/SEO";

type PublicPageResponse = {
  slug: string;
  title?: string | null;
  links_label?: string | null;
  show_links_label?: boolean | null;
  title_size?: "small" | "medium" | "large" | null;
  logo_base64?: string | null;
  description?: string | null;
  background_color?: string | null;
  instagram_url?: string | null;
  whatsapp_phone?: string | null;
  whatsapp_message?: string | null;
  links?: PageLink[] | null;
  social_links?: PageSocialLink[] | null;
};

function mapLinksForView(links: PageLink[] | null | undefined): PageLink[] {
  if (!links || links.length === 0) return [];
  return links.filter(link => Boolean(link.url));
}

export function PublicProfilePage({ slug }: { slug: string }) {
  const [page, setPage] = useState<PageViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    setPage(null);
    setHasActiveSubscription(true);

    if (!slug) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    api
      .getPublicPage(slug)
      .then(res => {
        if (!active) return;
        if (!res.ok || !res.data) {
          setNotFound(true);
          return;
        }

        const data = res.data as PublicPageResponse;
        const links = mapLinksForView(data.links);

        const viewData: PageViewData = {
          slug: data.slug,
          title: data.title || undefined,
          links_label: data.links_label || undefined,
          show_links_label: data.show_links_label,
          title_size: data.title_size || undefined,
          logo_base64: data.logo_base64 || undefined,
          description: data.description || undefined,
          background_color: data.background_color || undefined,
          instagram_url: data.instagram_url || undefined,
          whatsapp_phone: data.whatsapp_phone || undefined,
          whatsapp_message: data.whatsapp_message || undefined,
          links,
          social_links: data.social_links || undefined,
        };

        setPage(viewData);

        // Record page access asynchronously
        recordPageAccess(slug).catch(() => { });

        // Check subscription status
        getPageSubscriptionStatus(slug)
          .then(statusRes => {
            if (active && statusRes.ok && statusRes.data) {
              setHasActiveSubscription(statusRes.data.hasActiveSubscription);
            }
          })
          .catch(() => {
            // On error, default to showing no branding
            if (active) setHasActiveSubscription(true);
          });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">
        Carregando página...
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <SEO
          title="Página não encontrada | Hub Links"
          description="A página que você está procurando não foi encontrada."
          canonicalUrl={`https://hublinks.click/${slug}`}
        />
        <p className="text-2xl font-semibold text-neutral-800">
          Página não encontrada
        </p>
        <p className="mt-2 text-neutral-500 max-w-sm">
          Não encontramos uma página pública para este endereço.
        </p>
      </div>
    );
  }

  const profileTitle = page.title || page.slug;
  const profileDescription = page.description || `Confira os links e redes sociais de ${profileTitle}`;
  const profileUrl = `https://hublinks.click/${page.slug}`;

  return (
    <>
      <SEO
        title={`${profileTitle} | Hub Links`}
        description={profileDescription}
        keywords={`${page.slug}, links, redes sociais, perfil, bio`}
        ogImage={page.logo_base64 || "https://hublinks.click/og-profile.jpg"}
        ogType="profile"
        canonicalUrl={profileUrl}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "mainEntity": {
            "@type": "Person",
            "name": profileTitle,
            "description": profileDescription,
            "url": profileUrl,
            "image": page.logo_base64 || undefined,
            "sameAs": [
              page.instagram_url,
              ...(page.social_links?.map(link => link.url) || [])
            ].filter(Boolean)
          }
        }}
      />
      <PageView page={page} hasActiveSubscription={hasActiveSubscription} />
    </>
  );
}
